'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, X, ChevronsUpDown, Check } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import type { AvailabilitySlot } from '@/lib/services/guide-availability-service';
import { getTimezoneOptions } from '@/lib/constants/timezones';

const DAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const HOURS: string[] = [];
for (let h = 6; h <= 22; h++) {
  for (let m = 0; m < 60; m += 30) {
    HOURS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}

export const DEFAULT_AVAILABILITY: AvailabilitySlot[] = [
  { day_of_week: 1, start_time: '09:00', end_time: '17:00' },
  { day_of_week: 2, start_time: '09:00', end_time: '17:00' },
  { day_of_week: 3, start_time: '09:00', end_time: '17:00' },
  { day_of_week: 4, start_time: '09:00', end_time: '17:00' },
  { day_of_week: 5, start_time: '09:00', end_time: '17:00' },
];

interface AvailabilityEditorProps {
  slots: AvailabilitySlot[];
  timezone: string;
  onChange: (slots: AvailabilitySlot[], timezone: string) => void;
}

export default function AvailabilityEditor({ slots, timezone, onChange }: AvailabilityEditorProps) {
  const [tzOpen, setTzOpen] = useState(false);

  function addSlot() {
    const newSlot: AvailabilitySlot = {
      day_of_week: 1,
      start_time: '09:00',
      end_time: '17:00',
    };
    onChange([...slots, newSlot], timezone);
  }

  function removeSlot(index: number) {
    onChange(slots.filter((_, i) => i !== index), timezone);
  }

  function updateSlot(index: number, field: keyof AvailabilitySlot, value: number | string) {
    const updated = slots.map((slot, i) =>
      i === index ? { ...slot, [field]: value } : slot
    );
    onChange(updated, timezone);
  }

  function formatTime(t: string) {
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  }

  const tzOptions = getTimezoneOptions(timezone);
  const selectedTzLabel = tzOptions.find((tz) => tz.value === timezone)?.label || timezone;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium">Timezone</p>
        <p className="text-xs text-muted-foreground">
          All booking times will be shown in this zone for you and your guests. Change it if the detected one is incorrect.
        </p>
        <Popover open={tzOpen} onOpenChange={setTzOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={tzOpen}
              className="w-full max-w-md justify-between font-normal"
            >
              <span className="truncate">{selectedTzLabel}</span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full max-w-md p-0" align="start">
            <Command>
              <CommandInput placeholder="Search timezone..." />
              <CommandList>
                <CommandEmpty>No timezone found.</CommandEmpty>
                <CommandGroup>
                  {tzOptions.map((tz) => (
                    <CommandItem
                      key={tz.value}
                      value={tz.label}
                      onSelect={() => {
                        onChange(slots, tz.value);
                        setTzOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          timezone === tz.value ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      {tz.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="space-y-2">
        {slots.map((slot, i) => (
          <div key={i} className="flex items-center gap-2 flex-wrap">
            <Select
              value={String(slot.day_of_week)}
              onValueChange={(v) => updateSlot(i, 'day_of_week', parseInt(v))}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAYS.map((d) => (
                  <SelectItem key={d.value} value={String(d.value)}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={slot.start_time}
              onValueChange={(v) => updateSlot(i, 'start_time', v)}
            >
              <SelectTrigger className="w-[110px]">
                <SelectValue>{formatTime(slot.start_time)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {HOURS.map((h) => (
                  <SelectItem key={h} value={h}>
                    {formatTime(h)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <span className="text-muted-foreground">—</span>

            <Select
              value={slot.end_time}
              onValueChange={(v) => updateSlot(i, 'end_time', v)}
            >
              <SelectTrigger className="w-[110px]">
                <SelectValue>{formatTime(slot.end_time)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {HOURS.filter((h) => h > slot.start_time).map((h) => (
                  <SelectItem key={h} value={h}>
                    {formatTime(h)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => removeSlot(i)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      <Button variant="outline" size="sm" onClick={addSlot}>
        <Plus className="w-4 h-4 mr-1" /> Add time slot
      </Button>
    </div>
  );
}
